import { LoggingTarget, LogLevel } from '@trustedshops/tswp-core-common';
import { extractErrorFromPayload, serializeError, convertToError } from '../utils/error-serializer.util';

// Declare global ineum function
declare global {
  function ineum(command: string, ...args: any[]): void;

  interface Window {
    ineum?: (command: string, ...args: any[]) => void;
  }
}

export class InstanaLoggingTarget implements LoggingTarget {
  public constructor(
    private readonly _instanaLogLevels: LogLevel[],
  ) {}

  public trace(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Trace)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    ineum('reportEvent', this.formatMessage('trace', message), payload ?? {});
  }

  public debug(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Debug)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    ineum('reportEvent', this.formatMessage('debug', message), payload ?? {});
  }

  public information(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Information)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    ineum('reportEvent', this.formatMessage('information', message), payload ?? {});
  }

  public warning(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Warning)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    ineum('reportEvent', this.formatMessage('warning', message), payload ?? {});
  }

  public error(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Error)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    const errorFromPayload = extractErrorFromPayload(payload);

    if (errorFromPayload) {
      // Convert to Error instance for Instana stack trace extraction (handles HttpErrorResponse, etc.)
      const errorInstance = convertToError(errorFromPayload);
      // Pass the Error object directly as the second parameter so Instana can extract stack trace
      const errorPayload = {
        ...this.serializePayload(payload),
        error: serializeError(errorFromPayload)
      };
      ineum('reportError', errorInstance, errorPayload);
    } else {
      // Fallback to message when no error object is present
      ineum('reportError', this.formatMessage('error', message), payload ?? {});
    }
  }

  public fatal(message: string, payload?: any): void {
    if (!this.isLogLevelActive(LogLevel.Fatal)) {
      return;
    }

    if (!this.ineumExists()) {
      return;
    }

    const errorFromPayload = extractErrorFromPayload(payload);

    if (errorFromPayload) {
      // Convert to Error instance for Instana stack trace extraction (handles HttpErrorResponse, etc.)
      const errorInstance = convertToError(errorFromPayload);
      // Pass the Error object directly as the second parameter so Instana can extract stack trace
      const errorPayload = {
        ...this.serializePayload(payload),
        error: serializeError(errorFromPayload)
      };
      ineum('reportError', errorInstance, errorPayload);
    } else {
      // Fallback to message when no error object is present
      ineum('reportError', this.formatMessage('fatal', message), payload ?? {});
    }
  }

  private isLogLevelActive(logLevel: LogLevel): boolean {
    return this._instanaLogLevels.includes(logLevel);
  }

  private formatMessage(target: string, message: string): string {
    return `[${target.trim().toUpperCase()}] ${message}`;
  }

  private ineumExists(): boolean {
    return typeof window !== 'undefined' &&
           window.ineum !== undefined &&
           typeof window.ineum === 'function';
  }

  /**
   * Serializes payload, excluding the error object if it's in an array format from LogService.
   * LogService passes [error, payload] for error/fatal methods.
   */
  private serializePayload(payload: any): any {
    if (!payload) {
      return {};
    }

    // If payload is [error, actualPayload], return the actualPayload part
    // Only extract if array has at least 2 elements (error + payload)
    if (Array.isArray(payload) && payload.length > 1) {
      return payload[1] ?? {};
    }

    // If payload is an object with error property, exclude it to avoid duplication
    if (typeof payload === 'object' && payload.error) {
      const { error: _error, ...rest } = payload;
      return rest;
    }

    return payload;
  }
}
